import { apiGet, apiPost, getAuthHeaders, API_BASE } from '@/utils/api';

// Types
export type EdepReportType = 'STUDENTS' | 'TEACHERS' | 'STAFF';

export interface ProfileFieldStatus {
  field: string;
  label: string;
  completed: boolean;
  value?: string | null;
}

// Student physical document tracking (paper copies received)
export interface StudentPhysicalDocsStatus {
  physicalDocStudentCin: boolean;
  physicalDocStudentBac: boolean;
  physicalDocStudentReleveBac: boolean;
  physicalDocStudentDernierDiplome: boolean;
  physicalDocStudentReleveDiplome: boolean;
  physicalDocStudentReleveAnneesPrecedentes: boolean;
  physicalDocStudentCv: boolean;
  physicalDocStudentLettreMotivation: boolean;
}

export interface StudentProfileReport {
  id: string;
  userId: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  phoneCountry: string | null; // Country code indicator (e.g., "+212")
  campusId: string | null;
  campusName: string | null;
  academicYearId: string | null;
  academicYearName: string | null;
  codeFiliereInscrit: string | null;
  filiereInscrit: string | null;
  niveauFormation: number | null;
  niveauFormationInitial: number | null; // Level at first enrollment (never changes)
  diplomeAcces: string | null;
  premiereAnneeInscription: string | null; // First year of enrollment at this school
  isComplete: boolean;
  completedFieldsCount: number;
  totalRequiredFields: number;
  completionPercentage: number;
  fields: ProfileFieldStatus[];
  isAlsoStaff?: boolean;
  physicalDocs?: StudentPhysicalDocsStatus;
}

// Staff physical document tracking fields (paper copies received)
export interface PhysicalDocsStatus {
  physicalDocCin: boolean;
  physicalDocPassport: boolean;
  physicalDocCv: boolean;
  physicalDocContratTravail: boolean;
  physicalDocDiplome: boolean;
  physicalDocAttestationRib: boolean;
  physicalDocEngagementProfesseur: boolean;
  physicalDocReglementInterieur: boolean;
  physicalDocConsentement: boolean;
}

export interface StaffProfileReport {
  id: string;
  staffCode: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  phoneCountry: string | null; // Country code indicator (e.g., "+212")
  role: string;
  roleLabel: string;
  campuses: { id: string; name: string }[];
  academicYearId: string | null;
  academicYearName: string | null;
  isComplete: boolean;
  completedFieldsCount: number;
  totalRequiredFields: number;
  completionPercentage: number;
  fields: ProfileFieldStatus[];
  isAlsoProfessor: boolean;
  physicalDocs?: PhysicalDocsStatus;
}

export interface TeacherProfileReport extends StaffProfileReport {
  isAlsoStaff: boolean;
}

export interface EdepReportStats {
  total: number;
  complete: number;
  incomplete: number;
  completePercentage: number;
  incompletePercentage: number;
  byField: {
    field: string;
    label: string;
    completedCount: number;
    missingCount: number;
    completionPercentage: number;
  }[];
}

export interface EdepReportResponse {
  reportType: EdepReportType;
  generatedAt: string;
  generatedBy: {
    id: string;
    name: string;
  };
  filters: {
    campusId?: string;
    campusName?: string;
    academicYearId?: string;
    academicYearName?: string;
  };
  stats: EdepReportStats;
  data: StudentProfileReport[] | StaffProfileReport[] | TeacherProfileReport[];
}

export interface EdepReportHistoryItem {
  id: string;
  reportType: EdepReportType;
  reportName: string;
  campusId: string | null;
  campusName?: string;
  academicYearId: string | null;
  academicYearName?: string;
  totalCount: number;
  completeCount: number;
  incompleteCount: number;
  pdfUrl: string | null;
  excelUrl: string | null;
  generatedBy: string;
  generatedByName: string;
  createdAt: string;
}

export interface EdepSummary {
  students: EdepReportStats;
  teachers: EdepReportStats;
  staff: EdepReportStats;
  generatedAt: string;
}

/** An academic year the tracking page can be consulted for. */
export interface EdepAcademicYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface EdepAcademicYearsResponse {
  years: EdepAcademicYear[];
  /** The year in progress, derived from the institution's calendar parameters. */
  currentYearId: string | null;
  currentYearName: string | null;
  /**
   * The year the calendar parameters say we are in. When it differs from
   * currentYearName, the institution has not created that year yet and the page
   * is showing the previous one.
   */
  expectedCurrentYearName: string;
}

// API functions

export async function getEdepAcademicYears(): Promise<EdepAcademicYearsResponse> {
  return apiGet<EdepAcademicYearsResponse>('/reports/edep/academic-years');
}

export async function getEdepSummary(): Promise<EdepSummary> {
  return apiGet<EdepSummary>('/reports/edep/summary');
}

export async function getEdepReportPreview(
  reportType: EdepReportType,
  campusId?: string,
  academicYearId?: string,
): Promise<EdepReportResponse> {
  const params = new URLSearchParams();
  params.set('reportType', reportType);
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);

  return apiGet<EdepReportResponse>(`/reports/edep/preview?${params.toString()}`);
}

export async function generateEdepReport(
  reportType: EdepReportType,
  campusId?: string,
  academicYearId?: string,
  generatePdf?: boolean,
  generateExcel?: boolean,
): Promise<EdepReportHistoryItem> {
  return apiPost<EdepReportHistoryItem>('/reports/edep/generate', {
    reportType,
    campusId,
    academicYearId,
    generatePdf,
    generateExcel,
  });
}

export async function getEdepReportHistory(
  reportType?: EdepReportType,
  campusId?: string,
  academicYearId?: string,
  limit?: number,
  offset?: number,
): Promise<EdepReportHistoryItem[]> {
  const params = new URLSearchParams();
  if (reportType) params.set('reportType', reportType);
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));

  return apiGet<EdepReportHistoryItem[]>(`/reports/edep/history?${params.toString()}`);
}

export async function getEdepReportById(
  reportId: string,
): Promise<EdepReportHistoryItem & { reportData: EdepReportResponse }> {
  return apiGet<EdepReportHistoryItem & { reportData: EdepReportResponse }>(`/reports/edep/history/${reportId}`);
}

// Download functions for history reports

export async function downloadHistoryPdf(reportId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/reports/edep/download/${reportId}/pdf`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/pdf',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-${reportId}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadHistoryExcel(reportId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/reports/edep/download/${reportId}/excel`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download Excel');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-${reportId}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// Export functions

export async function downloadEdepExcel(
  reportType: EdepReportType,
  campusId?: string,
  academicYearId?: string,
  includeComplete?: boolean,
): Promise<void> {
  const params = new URLSearchParams();
  params.set('reportType', reportType);
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (includeComplete !== undefined) params.set('includeComplete', String(includeComplete));

  const response = await fetch(`${API_BASE}/reports/edep/export/excel?${params.toString()}`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export Excel');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-${reportType.toLowerCase()}-${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadEdepPdf(
  reportType: EdepReportType,
  campusId?: string,
  academicYearId?: string,
  includeComplete?: boolean,
): Promise<void> {
  const params = new URLSearchParams();
  params.set('reportType', reportType);
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (includeComplete !== undefined) params.set('includeComplete', String(includeComplete));

  const response = await fetch(`${API_BASE}/reports/edep/export/pdf?${params.toString()}`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/pdf',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-${reportType.toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// Multi-report exports

export async function downloadMultiExcel(
  types: EdepReportType[],
  campusId?: string,
  academicYearId?: string,
  includeComplete?: boolean,
): Promise<void> {
  const params = new URLSearchParams();
  params.set('types', types.join(','));
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (includeComplete !== undefined) params.set('includeComplete', String(includeComplete));

  const response = await fetch(`${API_BASE}/reports/edep/export/multi/excel?${params.toString()}`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export multi Excel');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-complet-${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function downloadMultiPdf(
  types: EdepReportType[],
  campusId?: string,
  academicYearId?: string,
  includeComplete?: boolean,
): Promise<void> {
  const params = new URLSearchParams();
  params.set('types', types.join(','));
  if (campusId) params.set('campusId', campusId);
  if (academicYearId) params.set('academicYearId', academicYearId);
  if (includeComplete !== undefined) params.set('includeComplete', String(includeComplete));

  const response = await fetch(`${API_BASE}/reports/edep/export/multi/pdf?${params.toString()}`, {
    method: 'GET',
    headers: {
      ...getAuthHeaders(),
      'Accept': 'application/pdf',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export multi PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rapport-edep-complet-${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
